import { useState } from "react";
import { toast } from "sonner";
import { Trash2, Pencil, X } from "lucide-react";
import api, { apiError } from "../../lib/api";

const EMPTY = { name: "", price: 499, duration_days: 30, description: "", active: true, featured: false, featured_days: 30, posts_per_day: 1,
  add_teachers: true, add_students: true, gallery: true, whatsapp: true, mcq: false, notes: true, max_listings: 1 };
const FLAGS = [["add_teachers", "Add teachers"], ["add_students", "Add students"], ["gallery", "Photo gallery"], ["whatsapp", "WhatsApp apply button"], ["notes", "Notes posts"], ["mcq", "MCQ test posts"], ["featured", "Featured badge"]];

export const packageSummary = (p) => [p.featured && `Featured ${p.featured_days || p.duration_days}d`, `${p.posts_per_day} post/day`, p.add_teachers && "teachers", p.add_students && "students",
  p.gallery && "gallery", p.whatsapp && "WhatsApp", p.mcq && "MCQ", p.notes && "notes", `${p.max_listings} listing${p.max_listings > 1 ? "s" : ""}`].filter(Boolean).join(" · ");

export default function PackagesTab({ packages, reload }) {
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.type === "number" ? Number(e.target.value) : e.target.value });
  const save = async () => {
    try {
      const r = await (editId ? api.put(`/admin/packages/${editId}`, form) : api.post("/admin/packages", form));
      toast.success(r.data.message); setForm(EMPTY); setEditId(null); reload();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete package "${p.name}"?`)) return;
    try { const r = await api.delete(`/admin/packages/${p.id}`); toast.success(r.data.message); reload(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div className="space-y-4">
        <div className="nb-flat p-4 text-sm" style={{ borderRadius: 12, background: "#F8FAFC" }}>
          <p className="font-bold">Free listing (always available)</p>
          <p className="text-slate-600 text-xs mt-1">Complete profile, courses & batches, receive verified reviews, appear in search & rankings. No posts, teachers, students, gallery or WhatsApp button.</p>
        </div>
        {packages.map((p) => (
          <div key={p.id} data-testid={`admin-pkg-${p.id}`} className="nb p-5 flex items-start justify-between gap-4" style={{ opacity: p.active ? 1 : 0.6 }}>
            <div className="min-w-0">
              <p className="font-display font-extrabold">{p.name} <span className="text-xs font-sans font-normal text-slate-500">₹{p.price} · live {p.duration_days} days{!p.active && " · inactive"}</span></p>
              <p className="text-xs text-slate-600 mt-1">{packageSummary(p)}</p>
              {p.description && <p className="text-xs text-slate-400 mt-1">{p.description}</p>}
            </div>
            <div className="flex gap-2 text-slate-400 shrink-0">
              <button data-testid={`admin-pkg-edit-${p.id}`} onClick={() => { setEditId(p.id); setForm({ ...EMPTY, ...p }); }} className="hover:text-[#171B33]"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`admin-pkg-delete-${p.id}`} onClick={() => del(p)} className="hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <div className="nb p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-extrabold">{editId ? "Edit Package" : "Create Package"}</h3>
          {editId && <button data-testid="pkg-cancel-edit" onClick={() => { setEditId(null); setForm(EMPTY); }}><X className="w-4 h-4 text-slate-400" /></button>}
        </div>
        <div className="space-y-3">
          <div><label className="nb-label">Name</label><input data-testid="pkg-name-input" value={form.name} onChange={set("name")} className="nb-input mt-1.5" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="nb-label">Price ₹</label><input data-testid="pkg-price-input" type="number" value={form.price} onChange={set("price")} className="nb-input mt-1.5" /></div>
            <div><label className="nb-label">Listing live (days)</label><input data-testid="pkg-days-input" type="number" value={form.duration_days} onChange={set("duration_days")} className="nb-input mt-1.5" /></div>
            <div><label className="nb-label">Posts / day</label><input data-testid="pkg-posts-input" type="number" min={0} value={form.posts_per_day} onChange={set("posts_per_day")} className="nb-input mt-1.5" /></div>
            <div><label className="nb-label">Featured days</label><input data-testid="pkg-featured-days-input" type="number" min={0} disabled={!form.featured} value={form.featured_days} onChange={set("featured_days")} className="nb-input mt-1.5" /></div>
            <div><label className="nb-label">Max listings</label><input data-testid="pkg-listings-input" type="number" min={1} value={form.max_listings} onChange={set("max_listings")} className="nb-input mt-1.5" /></div>
            <label className="flex items-center gap-2 text-sm font-bold pt-6"><input type="checkbox" data-testid="pkg-active-checkbox" checked={form.active} onChange={set("active")} /> Active</label>
          </div>
          <div>
            <label className="nb-label">Features included</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {FLAGS.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm nb-flat p-2" style={{ borderRadius: 10 }}>
                  <input type="checkbox" data-testid={`pkg-flag-${k}`} checked={!!form[k]} onChange={set(k)} /> {label}
                </label>
              ))}
            </div>
          </div>
          <div><label className="nb-label">Description</label><input data-testid="pkg-desc-input" value={form.description} onChange={set("description")} className="nb-input mt-1.5" /></div>
          <button data-testid="pkg-create-btn" onClick={save} className="nb-btn nb-btn-lime">{editId ? "Save changes" : "Create Package"}</button>
        </div>
      </div>
    </div>
  );
}
